// Health signals: simple rules over public data, each rated good / neutral / caution. They describe
// the numbers; they aren't advice.
import { sma, type Bar } from "./charts";
import { fixed, n, pct } from "./format";

export type Rating = "good" | "neutral" | "caution";
export interface Signal { group: string; name: string; value: string; rating: Rating; note: string }

export interface StockData {
  prices?: { price: number | null; high52: number | null; low52: number | null; bars: Bar[]; dividends: { t: number; amount: number }[] } | null;
  profile?: { summary: any; header: any; stats: any } | null;
  financials?: { currency: string | null; lines: Record<string, { date: string; value: number }[]> } | null;
  announcements?: { items: { date: string | null; sensitive: boolean }[] } | null;
}

const rate = (v: number, good: (v: number) => boolean, caution: (v: number) => boolean): Rating =>
  good(v) ? "good" : caution(v) ? "caution" : "neutral";

function returnOver(bars: Bar[], days: number): number | null {
  const last = bars.at(-1);
  if (!last) return null;
  const from = last.t - days * 86400;
  const start = bars.find((b) => b.t >= from);
  return start && start !== last ? last.c / start.c - 1 : null;
}

export function signals(d: StockData): Signal[] {
  const out: Signal[] = [];
  const add = (group: string, name: string, value: string, rating: Rating, note: string) => out.push({ group, name, value, rating, note });
  const bars = d.prices?.bars ?? [];
  const price = d.prices?.price ?? bars.at(-1)?.c ?? null;

  // Trend and momentum
  if (bars.length > 200 && price != null) {
    const s200 = sma(bars, 200).at(-1)!.value, s50 = sma(bars, 50).at(-1)!.value;
    const gap = price / s200 - 1;
    add("Trend", "Price vs 200-day average", pct(gap, true), rate(gap, (v) => v > 0.02, (v) => v < -0.02),
      gap >= 0 ? "Trading above its long-term average." : "Trading below its long-term average.");
    add("Trend", "50-day vs 200-day average", s50 >= s200 ? "Above" : "Below", s50 >= s200 ? "good" : "caution",
      s50 >= s200 ? "Medium-term trend is up (\"golden cross\" side)." : "Medium-term trend is down (\"death cross\" side).");
  }
  const r12 = returnOver(bars, 365);
  if (r12 != null) add("Trend", "12-month return", pct(r12, true), rate(r12, (v) => v > 0.1, (v) => v < -0.1), "Price change over the last year, excluding dividends.");
  if (price != null && d.prices?.high52) {
    const dd = price / d.prices.high52 - 1;
    add("Trend", "From 52-week high", pct(dd, true), rate(dd, (v) => v > -0.1, (v) => v < -0.3), "How far the price is below its high of the past year.");
  }
  const year = bars.filter((b) => b.t > (bars.at(-1)?.t ?? 0) - 365 * 86400);
  if (year.length > 100) {
    const rets = year.slice(1).map((b, i) => Math.log(b.c / year[i].c));
    const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
    const vol = Math.sqrt(rets.reduce((a, b) => a + (b - mean) ** 2, 0) / (rets.length - 1)) * Math.sqrt(252);
    add("Risk", "Volatility (1 year)", pct(vol), rate(vol, (v) => v < 0.25, (v) => v > 0.45), "Annualised swing in daily prices. Higher means bumpier.");
  }

  // Valuation and quality (Yahoo summary, falling back to ASX statistics)
  const s = d.profile?.summary ?? {};
  const stats = d.profile?.stats ?? {};
  const pe = n(s.summaryDetail?.trailingPE) ?? n(stats.priceEarningsRatio) ?? n(stats.peRatio);
  if (pe != null) add("Valuation", "Price / earnings", fixed(pe, 1), pe <= 0 ? "caution" : rate(pe, (v) => v < 15, (v) => v > 30),
    pe <= 0 ? "Earnings are negative." : "Lower means you pay less for each dollar of profit.");
  const pb = n(s.defaultKeyStatistics?.priceToBook);
  if (pb != null) add("Valuation", "Price / book", fixed(pb, 2), rate(pb, (v) => v > 0 && v < 1.5, (v) => v > 5 || v <= 0), "Price compared with the company's net assets per share.");

  const lines = d.financials?.lines ?? {};
  const latest = (k: string) => lines[k]?.at(-1)?.value ?? null;
  let de = n(s.financialData?.debtToEquity);
  if (de != null) de /= 100; // Yahoo sends it as a percentage
  else if (latest("TotalDebt") != null && latest("StockholdersEquity")) de = latest("TotalDebt")! / latest("StockholdersEquity")!;
  if (de != null) add("Balance sheet", "Debt / equity", fixed(de, 2), rate(de, (v) => v < 0.5, (v) => v > 1.5), "How much the company borrows relative to shareholders' funds.");
  const cr = n(s.financialData?.currentRatio);
  if (cr != null) add("Balance sheet", "Current ratio", fixed(cr, 2), rate(cr, (v) => v >= 1.5, (v) => v < 1), "Short-term assets over short-term debts. Below 1 can be tight.");

  const roe = n(s.financialData?.returnOnEquity);
  if (roe != null) add("Profitability", "Return on equity", pct(roe), rate(roe, (v) => v > 0.15, (v) => v < 0.05), "Profit generated on shareholders' funds.");
  const margin = n(s.financialData?.profitMargins);
  if (margin != null) add("Profitability", "Profit margin", pct(margin), rate(margin, (v) => v > 0.1, (v) => v < 0), "Share of revenue kept as profit.");
  const ni = latest("NetIncome");
  if (ni != null) add("Profitability", "Latest annual profit", ni >= 0 ? "Profit" : "Loss", ni >= 0 ? "good" : "caution", "Net income in the most recent annual report.");
  const fcf = latest("FreeCashFlow");
  if (fcf != null) add("Profitability", "Free cash flow", fcf >= 0 ? "Positive" : "Negative", fcf >= 0 ? "good" : "caution", "Cash left after running and investing in the business.");

  const rev = lines.TotalRevenue ?? [];
  if (rev.length >= 4 && rev.at(-4)!.value > 0 && rev.at(-1)!.value > 0) {
    const cagr = (rev.at(-1)!.value / rev.at(-4)!.value) ** (1 / 3) - 1;
    add("Growth", "Revenue growth (3-year average)", pct(cagr, true), rate(cagr, (v) => v > 0.05, (v) => v < 0), "Average yearly change in revenue over three years.");
  }

  // Dividends
  const divs = d.prices?.dividends ?? [];
  const thisYear = new Date().getFullYear();
  const paidYears = new Set(divs.map((x) => new Date(x.t * 1000).getFullYear()));
  const last5 = [1, 2, 3, 4, 5].filter((k) => paidYears.has(thisYear - k)).length;
  if (bars.length > 5 * 252) {
    add("Dividends", "Paid in the last 5 years", `${last5} of 5`, last5 === 5 ? "good" : last5 === 0 ? "neutral" : "caution",
      last5 === 0 ? "No dividends: common for growth or early-stage companies." : "Years with at least one dividend.");
  }
  const payout = n(s.summaryDetail?.payoutRatio);
  if (payout != null && payout > 0) add("Dividends", "Payout ratio", pct(payout), rate(payout, (v) => v < 0.75, (v) => v > 1), "Share of profit paid out. Over 100% isn't sustainable for long.");

  // Analysts
  const target = n(s.financialData?.targetMeanPrice);
  if (target != null && price) {
    const up = target / price - 1;
    add("Analysts", "Average target vs price", pct(up, true), rate(up, (v) => v > 0.1, (v) => v < -0.05),
      `${n(s.financialData?.numberOfAnalystOpinions) ?? "Some"} analysts' 12-month price targets.`);
  }

  // News flow
  const items = d.announcements?.items ?? [];
  if (items.length) {
    const month = Date.now() - 30 * 86400 * 1000;
    const recent = items.filter((a) => a.sensitive && a.date && Date.parse(a.date) > month).length;
    add("News", "Price-sensitive announcements (30 days)", String(recent), "neutral", "Worth reading: these can move the price.");
  }
  return out;
}
